import { FunctionComponent } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Flex,
  IconButton,
  Text,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { ChakraFeatherIcon } from '@/components/ChakraFeatherIcon';
import { TeamLite } from './types';
import { GroupTeamPicker } from './GroupTeamPicker';

const PREFIX = 'pages.application_season.components.season_step3_teams.groups';

type GroupTeamsCardProps = {
  groupName: string;
  /** Times atualmente alocados neste grupo (na ordem do form). */
  teams: TeamLite[];
  /** Times disponíveis (selected − alocados em qualquer grupo). */
  availableTeams: TeamLite[];
  isReadOnly?: boolean;
  onAdd: (teamUuid: string) => void;
  onRemove: (teamUuid: string) => void;
};

/** Cartão de um grupo (HYBRID) — lista os times alocados + botão de adicionar. */
export const GroupTeamsCard: FunctionComponent<GroupTeamsCardProps> = ({
  groupName,
  teams,
  availableTeams,
  isReadOnly,
  onAdd,
  onRemove,
}) => {
  const { t } = useTranslation();

  return (
    <Box borderWidth="1px" borderColor="gray.200" borderRadius="md" p={3}>
      <Flex align="center" justify="space-between" mb={3}>
        <Text fontWeight="semibold">{groupName}</Text>
        <Badge variant="subtle" colorScheme="purple" borderRadius="full" px={2}>
          {t(`${PREFIX}.team_count`, { count: teams.length })}
        </Badge>
      </Flex>

      <Flex direction="column" gap={1}>
        {teams.length === 0 ? (
          <Text fontSize="12px" color="gray.500" px={1} pb={2}>
            {t(`${PREFIX}.empty`)}
          </Text>
        ) : (
          teams.map(team => (
            <Flex
              key={team.uuid}
              align="center"
              gap={2}
              px={2}
              py={1.5}
              bg="purple.50"
              borderRadius="md"
            >
              <Avatar size="xs" name={team.acronym || team.name} src={team.logo_path} />
              <Text fontSize="13px" fontWeight="medium" flex="1" isTruncated>
                {team.name}
              </Text>
              {isReadOnly ? null : (
                <IconButton
                  aria-label={t(`${PREFIX}.remove`)}
                  icon={<ChakraFeatherIcon name="x" boxSize="12px" />}
                  size="xs"
                  variant="ghost"
                  color="gray.500"
                  onClick={() => onRemove(team.uuid)}
                />
              )}
            </Flex>
          ))
        )}

        {isReadOnly ? null : (
          <Box mt={teams.length > 0 ? 1 : 0}>
            <GroupTeamPicker
              availableTeams={availableTeams}
              buttonLabel={t(`${PREFIX}.add_team`)}
              onPick={onAdd}
            />
          </Box>
        )}
      </Flex>
    </Box>
  );
};
